import { Group, Stack } from "@mantine/core"
import { SortingState } from "@tanstack/react-table"
import { Loader } from "@tfg/ui"
import { Dispatch, SetStateAction, useState } from "react"
import z from "zod"

import { CountryIso3166Schema, LanguageIso6391 } from "@tfg/types/schemas/enum"
import { standardEmail } from "@tfg/types/schemas/utils"

import { useCustomReactTable } from "~/components/hooks/useCustomReactTable"
import { CustomTable } from "~/components/table/CustomTable"
import { TABLE_CONTEXT_MAP } from "~/components/table/views/ViewHeader"
import { Views } from "~/components/table/views/Views"
import {
  CreateBusinessPartnerRequestDto,
  createContactBusinessRelationshipRequestDtoTypeEnum,
  registrationIdRequestDtoTypeEnum,
  UpdateBusinessPartnerRequestDto,
} from "~/gen/api/types"
import { SupplierToolBar } from "~/pages/Brand/Suppliers/SuppliersRepository/components/SupplierToolBar"
import { useColumns } from "~/pages/Brand/Suppliers/SuppliersRepository/hooks/columns.tsx"
import { useFindContactsQuery } from "~/pages/Brand/Suppliers/SuppliersRepository/hooks/useFindContactsQuery"
import {
  errorMap,
  StringNoSpecialCharactersSchema,
} from "~/pages/SupplierPortal/TraceabilityRequests/components/ZodCustomErrorMap.tsx"

export type SupplierProps = {
  suppliersQuery: ReturnType<typeof useFindContactsQuery>
  searchSupplier: string
  setInitialValues: Dispatch<
    SetStateAction<
      CreateBusinessPartnerRequestDto | UpdateBusinessPartnerRequestDto
    >
  >
  setValuesUpdated: Dispatch<SetStateAction<boolean>>
  setFormType: Dispatch<SetStateAction<"create" | "update">>
  setSearchSupplier: Dispatch<SetStateAction<string>>
}

const registrationIdSchema = z.object({
  id: z.string().optional(),
  registrationId: z.string({ error: errorMap }),
  type: z.enum(registrationIdRequestDtoTypeEnum),
})

const businessRelationshipSchema = z.object({
  isActive: z.boolean().default(true),
  supplierCode: z.string().nullable(),
  type: z.enum(createContactBusinessRelationshipRequestDtoTypeEnum).nullable(),
  tagNames: z.array(z.string().max(16)).optional(),
})

export const formSchema = z.object({
  supplierId: z.string().optional(),
  name: StringNoSpecialCharactersSchema,
  email: standardEmail(),
  preferedLangageIso6391: z.enum(LanguageIso6391).default("EN"),
  organization: z.object({
    businessRelationship: businessRelationshipSchema,
    name: z.string({ error: errorMap }).min(2).max(100),
    street: z.string({ error: errorMap }).min(2).max(200),
    city: z.string().min(2).max(200),
    postalCode: z.string({ error: errorMap }).min(2).max(30),
    country: z.enum(CountryIso3166Schema.enum),
    registrationId: z.array(registrationIdSchema).default([]),
  }),
})

export type ContactFormType = z.infer<typeof formSchema>

export function Suppliers({
  suppliersQuery,
  setValuesUpdated,
  setInitialValues,
  setFormType,
  searchSupplier,
  setSearchSupplier,
}: SupplierProps) {
  const [sorting, setSorting] = useState<SortingState>([
    { id: "email", desc: false },
  ])
  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState(false)

  const columns = useColumns({
    setInitialValues,
    setValuesUpdated,
    setFormType,
  })

  const suppliers = suppliersQuery.data?.data ?? []
  const table = useCustomReactTable({
    data: suppliers,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
  })

  return (
    <Stack>
      <Views tableContext={TABLE_CONTEXT_MAP.BUSINESS_PARTNERS_REPOSITORY} />
      <Group w="100%" justify="left">
        <SupplierToolBar
          table={table}
          isColumnManagerOpen={isColumnManagerOpen}
          setIsColumnManagerOpen={setIsColumnManagerOpen}
          searchSupplier={searchSupplier}
          setSearchSupplier={setSearchSupplier}
        />
      </Group>
      {suppliersQuery.isLoading ? (
        <Loader fullPage />
      ) : (
        <CustomTable
          table={table}
          pagination={false}
          setOpenColumnManager={setIsColumnManagerOpen}
          isFetching={suppliersQuery.isFetching}
        />
      )}
    </Stack>
  )
}
